import { useEffect, useState } from "react";
import axios from "axios";

const fallback =
  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const isMobile = screenWidth <= 700;
  const isTablet = screenWidth <= 1050;

  useEffect(() => {
    carregarDashboard();
  }, []);

  useEffect(() => {
    const resize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  const carregarDashboard = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        "/dashboard/summary",
        {
          withCredentials: true
        }
      );

      setSummary(res.data);
    } catch (err) {
      console.log(err);
      alert("Erro ao carregar dashboard");
    }

    setLoading(false);
  };

  const money = (value) => {
    return Number(value || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    });
  };

  const number = (value) => {
    return Number(value || 0).toLocaleString("pt-BR");
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <p style={styles.loading}>
          Carregando dashboard...
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        ...styles.page,
        padding: isMobile ? 14 : 28
      }}
    >
      <div style={styles.header}>
        <p style={styles.tag}>
          HITC sistema
        </p>

        <div style={styles.headerRow}>
          <div>
            <h1
              style={{
                ...styles.title,
                fontSize: isMobile ? 28 : 38
              }}
            >
              Dashboard
            </h1>

            <p style={styles.subtitle}>
              Visão geral dos imóveis vendidos, imóveis à venda e colaboradores ativos.
            </p>
          </div>

          <button
            onClick={carregarDashboard}
            style={styles.refreshButton}
          >
            Atualizar
          </button>
        </div>
      </div>

      <div
        style={{
          ...styles.cardsGrid,
          gridTemplateColumns: isMobile
            ? "1fr"
            : isTablet
              ? "1fr 1fr"
              : "repeat(4, 1fr)"
        }}
      >
        <MetricCard
          icon="💰"
          label="Lucro total vendido"
          value={money(summary?.lucroTotalVendidos)}
          description="Soma do lucro de todos os imóveis vendidos"
          positive={Number(summary?.lucroTotalVendidos || 0) >= 0}
        />

        <MetricCard
          icon="✅"
          label="Imóveis vendidos"
          value={number(summary?.imoveisVendidos)}
          description="Total de imóveis já marcados como vendidos"
        />

        <MetricCard
          icon="🏘️"
          label="Imóveis à venda"
          value={number(summary?.imoveisAVenda)}
          description="Imóveis ativos que ainda não foram vendidos"
        />

        <MetricCard
          icon="👥"
          label="Colaboradores ativos"
          value={number(summary?.colaboradoresAtivos)}
          description="Baseado nos imóveis que estão à venda"
        />
      </div>

      <div
        style={{
          ...styles.contentGrid,
          gridTemplateColumns: isTablet ? "1fr" : "1.1fr .9fr"
        }}
      >
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>
                Resumo financeiro vendido
              </h2>

              <p style={styles.panelSubtitle}>
                Valores calculados com base nos imóveis vendidos.
              </p>
            </div>
          </div>

          <div style={styles.financeList}>
            <FinanceRow
              label="Valor total de vendas"
              value={money(summary?.valorTotalVendas)}
            />

            <FinanceRow
              label="Total de gastos dos vendidos"
              value={money(summary?.totalGastosVendidos)}
            />

            <FinanceRow
              label="Lucro total final"
              value={money(summary?.lucroTotalVendidos)}
              highlight
              positive={Number(summary?.lucroTotalVendidos || 0) >= 0}
            />
          </div>

          <div style={styles.soldBox}>
            <div style={styles.soldHeader}>
              <h3 style={styles.smallTitle}>
                Últimos imóveis vendidos
              </h3>

              <span style={styles.countBadge}>
                {summary?.vendidosDetalhes?.length || 0} vendido(s)
              </span>
            </div>

            {summary?.vendidosDetalhes?.length === 0 && (
              <div style={styles.emptyText}>
                Nenhum imóvel vendido ainda.
              </div>
            )}

            <div style={styles.soldList}>
              {(summary?.vendidosDetalhes || []).map((item) => (
                <div key={item.id} style={styles.soldItem}>
                  <div style={styles.soldInfo}>
                    <strong>
                      {item.nome || "Imóvel sem nome"}
                    </strong>

                    <span>
                      {item.bairro || "Bairro não informado"} •{" "}
                      {item.cidade || "Cidade não informada"}
                    </span>
                  </div>

                  <div style={styles.soldValues}>
                    <span>
                      Venda: {money(item.valor_venda)}
                    </span>

                    <span>
                      Gastos: {money(item.total_gastos)}
                    </span>

                    <b
                      style={{
                        color: Number(item.lucro || 0) >= 0
                          ? "#86efac"
                          : "#fca5a5"
                      }}
                    >
                      Lucro: {money(item.lucro)}
                    </b>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>
                Colaboradores em atividade
              </h2>

              <p style={styles.panelSubtitle}>
                Usuários ligados aos imóveis que ainda estão à venda.
              </p>
            </div>
          </div>

          {summary?.colaboradores?.length === 0 && (
            <div style={styles.emptyText}>
              Nenhum colaborador ativo encontrado.
            </div>
          )}

          <div style={styles.collaboratorList}>
            {(summary?.colaboradores || []).map((item) => (
              <div key={item.id} style={styles.collaboratorItem}>
                <img
                  src={item.foto || fallback}
                  alt={item.nome}
                  style={styles.avatar}
                />

                <div style={styles.collaboratorInfo}>
                  <strong>
                    {item.nome || "Usuário"}
                  </strong>

                  <span>
                    {item.email}
                  </span>
                </div>

                <div style={styles.activityBadge}>
                  {item.totalImoveis} imóvel(is)
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  description,
  positive
}) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricTop}>
        <div style={styles.metricIcon}>
          {icon}
        </div>
      </div>

      <p style={styles.metricLabel}>
        {label}
      </p>

      <h2
        style={{
          ...styles.metricValue,
          color:
            positive === undefined
              ? "#f8fafc"
              : positive
                ? "#86efac"
                : "#fca5a5"
        }}
      >
        {value}
      </h2>

      <p style={styles.metricDescription}>
        {description}
      </p>
    </div>
  );
}

function FinanceRow({
  label,
  value,
  highlight,
  positive
}) {
  return (
    <div
      style={{
        ...styles.financeRow,
        background: highlight
          ? "rgba(34,197,94,0.10)"
          : "#111827",
        border: highlight
          ? "1px solid rgba(34,197,94,0.25)"
          : "1px solid rgba(255,255,255,0.06)"
      }}
    >
      <span>
        {label}
      </span>

      <strong
        style={{
          color:
            positive === undefined
              ? "#f8fafc"
              : positive
                ? "#86efac"
                : "#fca5a5"
        }}
      >
        {value}
      </strong>
    </div>
  );
}

const styles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    background: "#0f172a",
    color: "#fff",
    boxSizing: "border-box"
  },

  loading: {
    color: "#cbd5e1",
    padding: 30
  },

  header: {
    marginBottom: 24
  },

  tag: {
    color: "#60a5fa",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: 1.4,
    marginBottom: 8
  },

  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 18,
    flexWrap: "wrap"
  },

  title: {
    margin: 0,
    color: "#f8fafc",
    fontWeight: 800,
    lineHeight: 1.1
  },

  subtitle: {
    marginTop: 10,
    color: "#94a3b8",
    fontSize: 15,
    maxWidth: 780,
    lineHeight: 1.5
  },

  refreshButton: {
    padding: "11px 14px",
    borderRadius: 12,
    border: "1px solid rgba(96,165,250,0.35)",
    background: "rgba(37,99,235,0.16)",
    color: "#bfdbfe",
    cursor: "pointer",
    fontWeight: "bold"
  },

  cardsGrid: {
    display: "grid",
    gap: 16,
    marginBottom: 22
  },

  metricCard: {
    background: "linear-gradient(180deg, #111827, #020617)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 22,
    padding: 20,
    boxSizing: "border-box",
    boxShadow: "0 20px 60px rgba(0,0,0,0.28)"
  },

  metricTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16
  },

  metricIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    background: "rgba(37,99,235,0.16)",
    border: "1px solid rgba(96,165,250,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20
  },

  metricLabel: {
    margin: 0,
    color: "#94a3b8",
    fontSize: 13
  },

  metricValue: {
    margin: "8px 0 0",
    fontSize: 28,
    lineHeight: 1.05
  },

  metricDescription: {
    margin: "10px 0 0",
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.4
  },

  contentGrid: {
    display: "grid",
    gap: 18,
    alignItems: "start"
  },

  panel: {
    background: "rgba(15,23,42,0.95)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 24,
    padding: 22,
    boxSizing: "border-box",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)"
  },

  panelHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 18
  },

  panelTitle: {
    margin: 0,
    color: "#f8fafc",
    fontSize: 21
  },

  panelSubtitle: {
    margin: "7px 0 0",
    color: "#94a3b8",
    fontSize: 13,
    lineHeight: 1.4
  },

  financeList: {
    display: "flex",
    flexDirection: "column",
    gap: 10
  },

  financeRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    padding: 14,
    borderRadius: 14,
    color: "#cbd5e1",
    fontSize: 14
  },

  soldBox: {
    marginTop: 20
  },

  soldHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 12
  },

  smallTitle: {
    margin: 0,
    color: "#f8fafc",
    fontSize: 17
  },

  countBadge: {
    background: "rgba(37,99,235,0.15)",
    color: "#93c5fd",
    padding: "7px 10px",
    borderRadius: 999,
    fontSize: 12,
    whiteSpace: "nowrap"
  },

  soldList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxHeight: 360,
    overflowY: "auto",
    paddingRight: 6
  },

  soldItem: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    background: "#111827",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 16,
    padding: 14,
    flexWrap: "wrap"
  },

  soldInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0
  },

  soldValues: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    color: "#cbd5e1",
    fontSize: 13,
    minWidth: 160
  },

  collaboratorList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxHeight: 470,
    overflowY: "auto",
    paddingRight: 6
  },

  collaboratorItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#111827",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 16,
    padding: 13
  },

  avatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0
  },

  collaboratorInfo: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4
  },

  activityBadge: {
    background: "rgba(34,197,94,0.12)",
    color: "#86efac",
    padding: "7px 10px",
    borderRadius: 999,
    fontSize: 12,
    whiteSpace: "nowrap"
  },

  emptyText: {
    padding: 14,
    borderRadius: 14,
    background: "#111827",
    color: "#94a3b8",
    border: "1px solid rgba(255,255,255,0.06)",
    fontSize: 14
  }
};